import { ChangeEvent, useMemo, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { TEXT_POSITION_OPTIONS } from "@constants/text-position-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ResourceCategoryTypes } from "@enums/resource-category-types";
import { SelectOption } from "@idw-types/common";
import { AXNUIContent, AXNUIContentFormPanel, AXNUILayout, AXNUILayoutFormPanel } from "@iddataweb/idw-common-ui/types";
import { ThemingFormPanelValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { RadioInputFormGroup } from "../../../../../common/radio-input-form-group/radio-input-form-group";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { FileInputFormGroup } from "../../../../../common/file-input-form-group/file-input-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingFormPanelLogoSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUILayout: AXNUILayout;
    pageLevel: PageLevelTypes;
    validationErrors: ThemingFormPanelValidationErrors;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    updateAXNUIContentFormPanelFieldValue: (key: keyof AXNUIContentFormPanel, value: string) => void;
    updateAXNUILayoutFormPanelFieldValue: (key: keyof AXNUILayoutFormPanel, value: string | boolean) => void;
};

export const ThemingFormPanelLogoSection = (props: ThemingFormPanelLogoSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiContent,
        axnuiLayout,
        inheritedAXNUIContent,
        inheritedAXNUILayout,
        pageLevel,
        validationErrors,
        uploadFileToS3,
        updateAXNUIContentFormPanelFieldValue,
        updateAXNUILayoutFormPanelFieldValue
    } = props;

    const [formPanelLogoUploadInProgress, setFormPanelLogoUploadInProgress] = useState(false);

    const handleAddFormPanelLogoChange = (newValue: string) => {
        updateAXNUILayoutFormPanelFieldValue("showFormPanelLogo", isStringTrue(newValue));
    };

    const handleResetAddFormPanelLogo = () => {
        updateAXNUILayoutFormPanelFieldValue("showFormPanelLogo", inheritedAXNUILayout.formPanel.showFormPanelLogo);
    };

    const handleFormPanelLogoChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files.length > 0) {
            const file = files[0];
            //upload to s3
            setFormPanelLogoUploadInProgress(true);
            const uploadResult = await uploadFileToS3(file, ResourceCategoryTypes.LOGO);
            if (uploadResult) {
                updateAXNUILayoutFormPanelFieldValue("formPanelLogoUrl", uploadResult);
            }
            setFormPanelLogoUploadInProgress(false);
        }
    };

    const handleResetFormPanelLogo = () => {
        updateAXNUILayoutFormPanelFieldValue("formPanelLogoUrl", inheritedAXNUILayout.formPanel.formPanelLogoUrl || "");
    };

    const handleFormPanelLogoPositionChange = (newValue: SelectOption) => {
        updateAXNUILayoutFormPanelFieldValue("formPanelAlignLogo", newValue.label);
    };

    const handleResetFormPanelLogoPosition = () => {
        updateAXNUILayoutFormPanelFieldValue("formPanelAlignLogo", inheritedAXNUILayout.formPanel.formPanelAlignLogo);
    };

    const handleFormPanelLogoAltTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFormPanelFieldValue("logoAltText", event.target.value);
    };

    const handleResetFormPanelLogoAltText = () => {
        updateAXNUIContentFormPanelFieldValue("logoAltText", inheritedAXNUIContent.formPanel.logoAltText);
    };

    const handleFormPanelLogoWidthChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUILayoutFormPanelFieldValue("formPanelLogoWidth", event.target.value);
    };

    const handleResetFormPanelLogoWidth = () => {
        updateAXNUILayoutFormPanelFieldValue("formPanelLogoWidth", inheritedAXNUILayout.formPanel.formPanelLogoWidth);
    };

    const handleFormPanelLogoHeightChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUILayoutFormPanelFieldValue("formPanelLogoHeight", event.target.value);
    };

    const handleResetFormPanelLogoHeight = () => {
        updateAXNUILayoutFormPanelFieldValue("formPanelLogoHeight", inheritedAXNUILayout.formPanel.formPanelLogoHeight);
    };

    const selectedFormPanelLogoPositionOption = useMemo(() => {
        if (axnuiLayout.formPanel.formPanelAlignLogo) {
            return TEXT_POSITION_OPTIONS.find(po => po.label === axnuiLayout.formPanel.formPanelAlignLogo);
        }
        return null;
    }, [axnuiLayout.formPanel.formPanelAlignLogo]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUILayout.formPanel.showFormPanelLogo}
                currentThemingPropertyValue={axnuiLayout.formPanel.showFormPanelLogo}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`theming-formPanel-add-formPanel-logo-radio-input`}
                    automationId={`theming-formPanel-add-formPanel-logo-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.addFormPanelLogo.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAddFormPanelLogoChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={axnuiLayout.formPanel.showFormPanelLogo ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.addFormPanelLogo.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetAddFormPanelLogo}
                />
            </ThemingFormGroupWrapper>
            {axnuiLayout.formPanel.showFormPanelLogo && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUILayout.formPanel.formPanelLogoUrl}
                        currentThemingPropertyValue={axnuiLayout.formPanel.formPanelLogoUrl}
                        pageLevel={pageLevel}
                    >
                        <FileInputFormGroup
                            controlId={"theming-form-panel-logo-input"}
                            automationId={"theming-form-panel-logo-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogo.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogo.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleFormPanelLogoChange}
                            value={axnuiLayout.formPanel.formPanelLogoUrl || ""}
                            accept={"image/png, image/gif, image/jpeg, image/jpg"}
                            disabled={isDisabled}
                            uploadInProgress={formPanelLogoUploadInProgress}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogo.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetFormPanelLogo}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.formPanelLogoError}
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUILayout.formPanel.formPanelAlignLogo}
                        currentThemingPropertyValue={axnuiLayout.formPanel.formPanelAlignLogo}
                        pageLevel={pageLevel}
                    >
                        <SelectFormGroup
                            controlId={"theming-form-panel-text-postition-select-input"}
                            automationId={"theming-form-panel-text-position-select-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoPosition.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoPosition.label}
                            disabled={isDisabled}
                            isClearable={false}
                            isRequired={true}
                            isSearchable={false}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleFormPanelLogoPositionChange}
                            options={TEXT_POSITION_OPTIONS}
                            selectedOption={selectedFormPanelLogoPositionOption}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoPosition.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetFormPanelLogoPosition}
                        />
                    </ThemingFormGroupWrapper>

                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUILayout.formPanel.formPanelLogoWidth}
                        currentThemingPropertyValue={axnuiLayout.formPanel.formPanelLogoWidth}
                        pageLevel={pageLevel}
                    >
                        <InputFormGroup
                            controlId={"theming-form-panel-logo-width-text-input"}
                            automationId={"theming-form-panel-logo-width-text-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoWidth.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoWidth.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleFormPanelLogoWidthChange}
                            value={axnuiLayout.formPanel.formPanelLogoWidth || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoWidth.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetFormPanelLogoWidth}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.formPanelLogoWidthError}
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUILayout.formPanel.formPanelLogoHeight}
                        currentThemingPropertyValue={axnuiLayout.formPanel.formPanelLogoHeight}
                        pageLevel={pageLevel}
                    >
                        <InputFormGroup
                            controlId={"theming-form-panel-logo-height-text-input"}
                            automationId={"theming-form-panel-logo-height-text-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoHeight.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoHeight.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleFormPanelLogoHeightChange}
                            value={axnuiLayout.formPanel.formPanelLogoHeight || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoHeight.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetFormPanelLogoHeight}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.formPanelLogoHeightError}

                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.formPanel.logoAltText}
                        currentThemingPropertyValue={axnuiContent.formPanel.logoAltText}
                        pageLevel={pageLevel}
                    >
                        <InputFormGroup
                            controlId={"theming-form-panel-logo-alt-text-input"}
                            automationId={"theming-form-panel-logo-alt-text-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoAltText.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoAltText.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleFormPanelLogoAltTextChange}
                            value={axnuiContent.formPanel.logoAltText || ""}
                            disabled={isDisabled}
                            isRequired={false}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.inputs.formPanelLogoAltText.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetFormPanelLogoAltText}
                        />
                    </ThemingFormGroupWrapper>
                </>
            )}
        </div>
    );
};